import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Swords, Trophy, Share2, Check } from "lucide-react";
import Navbar from "../components/Navbar";
import GameCanvas from "../game/GameCanvas";
import api from "../lib/api";
import { generateDailyLevel } from "../lib/levels";

export default function Challenge() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [ch, setCh] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const load = () =>
    api
      .get(`/challenges/${token}`)
      .then((r) => setCh(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const level = useMemo(() => (ch ? generateDailyLevel(ch.seed) : null), [ch]);

  const onComplete = async (score) => {
    try {
      const r = await api.post(`/challenges/${token}/submit`, { score });
      setCh({ ...ch, scores: r.data.scores });
    } catch (_) {
      /* ignore */
    }
  };

  const share = async () => {
    const url = window.location.href;
    const text = `${ch?.from_name || "A pilot"} challenged you on Cosmic Worm Breaker. Can you beat their score?`;
    try {
      if (navigator.share) await navigator.share({ title: "Cosmic Worm Breaker", text, url });
      else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch (_) {
      /* noop */
    }
  };

  if (err) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 min-h-screen flex items-center justify-center px-6">
          <div className="glass p-8 text-center max-w-md" data-testid="challenge-error">
            <div className="font-heading uppercase text-2xl text-[#ff007f] mb-2">
              Challenge Not Found
            </div>
            <p className="text-white/60 text-sm">{err}</p>
          </div>
        </main>
      </div>
    );
  }

  if (!ch || !level) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest">
          LOADING CHALLENGE...
        </main>
      </div>
    );
  }

  const challengeLevel = { ...level, name: `Challenge · ${ch.from_name}` };

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-16 fade-in">
        <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 font-mono-cosmic text-[11px] tracking-[0.35em] text-[#ff007f] uppercase mb-2">
              <Swords className="w-3.5 h-3.5" />
              Challenge from {ch.from_name}
            </div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter">
              Beat Their Score
            </h1>
            {ch.message && <p className="text-white/70 text-sm mt-2 italic">"{ch.message}"</p>}
          </div>
          <button onClick={share} className="btn-neon cyan" data-testid="challenge-share-btn">
            <span className="inline-flex items-center gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? "Copied!" : "Share"}
            </span>
          </button>
        </header>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <GameCanvas
            key={token}
            level={1}
            levelOverride={challengeLevel}
            isDaily
            onComplete={onComplete}
            onExit={() => navigate("/levels")}
            bestScore={ch.scores?.[0]?.score || 0}
          />

          <aside className="glass p-6 self-start" data-testid="challenge-leaderboard">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-4 h-4 text-[#ffd700]" />
              <div className="font-heading uppercase tracking-[0.25em] text-sm text-white">
                Challengers
              </div>
            </div>
            {ch.scores.length === 0 ? (
              <div className="text-white/50 font-mono-cosmic text-xs tracking-widest uppercase">
                No attempts yet. Be first.
              </div>
            ) : (
              <ol className="space-y-2">
                {ch.scores.map((row, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="font-heading font-black w-7 text-lg"
                        style={{
                          color:
                            i === 0 ? "#ffd700" : i === 1 ? "#00ffff" : i === 2 ? "#ff007f" : "#8b949e",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="truncate text-white/90 text-sm">{row.name}</span>
                    </div>
                    <span className="font-mono-cosmic text-[11px] tracking-widest text-[#00ffff]">
                      {row.score.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
